const MAX_PAGES_TO_FETCH_LIMIT = 10;

export interface PaginationRequestData {
    pageSize: number;
    pageNumber: number;
}

export interface PaginationResponse<T> {
    pageCount?: number;
    entities?: T[];
}

export type ApiCallFunctionWithOtherQuery<T, Q = Record<string, any>> = (
    requestData: PaginationRequestData & Q
) => Promise<PaginationResponse<T>>;

/**
 * Generic function to fetch only up to maxPages pages of data from a paginated API with additional query parameters
 * @param apiCall - Function that makes the API call with pagination and query parameters
 * @param queryParams - Additional query parameters to pass to the API call
 * @param pageSize - Number of items per page (default: 100)
 * @param numOfPagesToFetch - Number of pages to fetch (default: 1)
 * @returns Promise with all entities from all pages
 */
export async function fetchPagesWithQuery<T, Q = Record<string, any>>(
    apiCall: ApiCallFunctionWithOtherQuery<T, Q>,
    queryParams: Q,
    pageSize = 100,
    numOfPagesToFetch = 1
): Promise<T[]> {
    const allEntities: T[] = [];
    const maxPages = Math.min(
        Math.max(numOfPagesToFetch, 1),
        MAX_PAGES_TO_FETCH_LIMIT
    );

    for (let pageNumber = 1; pageNumber <= maxPages; pageNumber++) {
        try {
            const { entities } = await apiCall({
                pageSize,
                pageNumber,
                ...queryParams,
            });

            if (entities) {
                allEntities.push(...entities);
            }

            // Stop if the page size is greater than the current size being returned,
            // e.g. if the page size is 100 and the page count is 99,
            // then we have fetched all the pages.
            if ((entities || []).length < pageSize) {
                break;
            }
        } catch (error) {
            console.error(`Failed to fetch page ${pageNumber}:`, error);
            // Stop fetching on API failure to prevent infinite loops
            break;
        }
    }

    return allEntities;
}
